import axios, {
  AxiosError,
  AxiosInstance,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import getConfig from 'next/config';
import {
  camelCase,
  snakeCase,
} from 'change-case';

import {
  caseConverter,
  CaseConverter,
} from '@/utils/case-converter';

interface ErrorResponse {
  // 'non_field_errors` is a potential top-level error key
  // from the API response.
  non_field_errors?: Array<string>;
  [key: string]: unknown;
}

const { REACT_APP_BACKEND_URL } = getConfig().publicRuntimeConfig;

class Request {
  private instance!: AxiosInstance;

  constructor() {
    this.initConfig();
  }

  initConfig() {
    this.instance = axios.create({
      baseURL: REACT_APP_BACKEND_URL,
    });

    this.instance.interceptors.request.use(async config => ({
      ...config,
      data: config.data ? caseConverter(config.data, snakeCase) : config.data,
      params: config.params ? caseConverter(config.params, snakeCase) : config.params,
    }));

    this.instance.interceptors.response.use(response => ({
      ...response,
      data: caseConverter(response.data, camelCase),
    }), async error => {
      if (!error.response) {
        return Promise.reject(new Error('Error do not have a response.'));
      }

      // Return any error which is not due to authentication back to the calling service
      if (error.response.status !== 401) {
        return Promise.reject(this.parseError(error));
      }

      const { config } = error;

      return this.instance(config)
        .then(res => Promise.resolve({
          ...res,
          data: caseConverter(res.data, camelCase),
        }))
        .catch(err => Promise.reject(this.parseError(err)));
    });
  }

  parseError(error: AxiosError): AxiosError {
    const { response } = error;

    const convertedData = response?.data ?
      caseConverter(response.data as CaseConverter, camelCase) :
      null;
    let data: unknown;

    if (typeof convertedData === 'string' || typeof convertedData === 'number' || typeof convertedData === 'boolean' || typeof convertedData === 'undefined' || convertedData === null) {
      data = convertedData;
    } else {
      const errorData = response?.data as ErrorResponse;
      const nonFieldErorrs: Array<string> = errorData?.non_field_errors || [];

      data = {
        ...convertedData,
        _error: nonFieldErorrs,
      };
    }

    return {
      ...error,
      response: {
        ...response,
        data,
      } as AxiosResponse,
    };
  }

  get<P, QP = unknown>(
    url: string,
    config?: AxiosRequestConfig & { params?: QP }
  ): AxiosPromise<P> {
    return this.instance.get(url, config);
  }

  post<P>(url: string, data?: unknown, config?: AxiosRequestConfig): AxiosPromise<P> {
    return this.instance.post(url, data, config);
  }

  options<P>(url: string, config?: AxiosRequestConfig): AxiosPromise<P> {
    return this.instance.options(url, config);
  }

  patch<P>(url: string, data?: unknown, config?: AxiosRequestConfig): AxiosPromise<P> {
    return this.instance.patch(url, data, config);
  }

  put<P>(url: string, data?: unknown, config?: AxiosRequestConfig): AxiosPromise<P> {
    return this.instance.put(url, data, config);
  }

  delete<P>(url: string, config?: AxiosRequestConfig): AxiosPromise<P> {
    return this.instance.delete(url, config);
  }
}

export const request = new Request();
